import React, { useEffect, useMemo, useState } from 'react';
import { BookmarkPlus, MapPin, Pencil, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import { v1Patch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  INTEGRITY_CATALOG_VERSION,
  INTEGRITY_CATEGORIES,
  INTEGRITY_DISPOSITIONS,
  INTEGRITY_REVIEW_DEPTHS,
  INTEGRITY_TAG_BY_CODE,
  INTEGRITY_TAGS,
  dispositionToneClass,
  formatIntegrityTime,
  severityChipClass,
  suggestedDisposition,
} from '@/lib/integrityTagCatalog';
import { fmtDateTimeIST } from '@/lib/format';
import { classNames } from '@/lib/format';
import { toastMutationError } from '@/lib/resumeEnrichmentErrors';

/** Tags meaningful without footage (Process only). */
const NO_RECORDING_TAG_CODES = new Set([
  'recording_incomplete',
  'suspected_external_help',
]);

function DispositionBadge({ disposition }) {
  const label = INTEGRITY_DISPOSITIONS.find((d) => d.value === disposition)?.label || 'Not reviewed';
  return (
    <span
      className={classNames(
        'inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold',
        dispositionToneClass(disposition),
      )}
      data-testid="integrity-disposition-badge"
    >
      {disposition ? label : 'Not reviewed'}
    </span>
  );
}

function depthLabel(value) {
  return INTEGRITY_REVIEW_DEPTHS.find((d) => d.value === value)?.label || value || '—';
}

/**
 * Staff-only MCQ / Tech session integrity review panel.
 */
export function IntegrityReviewPanel({
  candidateId,
  assessmentId,
  stageCode = 'MCQ',
  integrityReview,
  recordingAvailable = false,
  videoRef,
  currentTimeSec = 0,
  readOnly = false,
  onChanged,
  testIdPrefix = 'mcq',
}) {
  const [tagCodes, setTagCodes] = useState([]);
  const [disposition, setDisposition] = useState(null);
  const [reviewDepth, setReviewDepth] = useState(
    recordingAvailable ? 'watched_full' : 'recording_unavailable',
  );
  const [notes, setNotes] = useState('');
  const [markers, setMarkers] = useState([]);
  const [markerNote, setMarkerNote] = useState('');
  const [markerTag, setMarkerTag] = useState('');
  const [saving, setSaving] = useState(false);
  const [dispTouched, setDispTouched] = useState(false);
  const [editing, setEditing] = useState(false);

  const submitted = Boolean(integrityReview?.reviewed_at);
  // Closed-eval / external readOnly always blocks edits; locked view also blocks form.
  const formDisabled = readOnly || (submitted && !editing);

  const hydrateFromReview = (srcIn, { keepEditing = false } = {}) => {
    const src = srcIn || {};
    let codes = Array.isArray(src.tag_codes) ? [...src.tag_codes] : [];
    if (!recordingAvailable) {
      codes = codes.filter((c) => NO_RECORDING_TAG_CODES.has(c));
    }
    setTagCodes(codes);
    if (src.disposition) {
      setDisposition(src.disposition);
      setDispTouched(true);
    } else if (recordingAvailable) {
      setDisposition(suggestedDisposition(codes) || 'clear');
      setDispTouched(false);
    } else {
      setDisposition(null);
      setDispTouched(false);
    }
    setReviewDepth(
      recordingAvailable
        ? (src.review_depth || 'watched_full')
        : 'recording_unavailable',
    );
    setNotes(src.notes || '');
    setMarkers(
      recordingAvailable && Array.isArray(src.markers)
        ? src.markers.map((m) => ({ ...m }))
        : [],
    );
    setMarkerNote('');
    setMarkerTag('');
    if (!keepEditing) setEditing(false);
  };

  useEffect(() => {
    hydrateFromReview(integrityReview);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when review/assessment changes
  }, [integrityReview, recordingAvailable, assessmentId]);

  const suggested = useMemo(() => suggestedDisposition(tagCodes), [tagCodes]);

  useEffect(() => {
    if (!dispTouched && recordingAvailable && !formDisabled) setDisposition(suggested);
  }, [suggested, dispTouched, recordingAvailable, formDisabled]);

  const visibleTags = useMemo(() => {
    if (recordingAvailable) return INTEGRITY_TAGS;
    return INTEGRITY_TAGS.filter((t) => NO_RECORDING_TAG_CODES.has(t.code));
  }, [recordingAvailable]);

  const tagsByCategory = useMemo(() => {
    const map = {};
    for (const tag of visibleTags) {
      if (!map[tag.category]) map[tag.category] = [];
      map[tag.category].push(tag);
    }
    return map;
  }, [visibleTags]);

  const visibleCategories = useMemo(
    () => INTEGRITY_CATEGORIES.filter((cat) => (tagsByCategory[cat] || []).length > 0),
    [tagsByCategory],
  );

  const patchPath = stageCode === 'TECH_ASSESSMENT'
    ? `/candidates/${candidateId}/tech-assessment/assessment/${assessmentId}/integrity-review`
    : `/candidates/${candidateId}/mcq/assessment/${assessmentId}/integrity-review`;

  const toggleTag = (code) => {
    if (formDisabled) return;
    if (!recordingAvailable && !NO_RECORDING_TAG_CODES.has(code)) return;
    setTagCodes((prev) => (
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    ));
  };

  const addMarker = () => {
    if (formDisabled) return;
    if (!recordingAvailable) {
      toast.error('No recording available to mark');
      return;
    }
    const t = videoRef?.current?.getCurrentTime?.() ?? currentTimeSec ?? 0;
    videoRef?.current?.openPlayer?.();
    setMarkers((prev) => [
      ...prev,
      {
        t_sec: Math.max(0, Math.floor(t)),
        note: markerNote.trim(),
        tag_code: markerTag || null,
      },
    ].sort((a, b) => a.t_sec - b.t_sec));
    setMarkerNote('');
    setMarkerTag('');
  };

  const removeMarker = (idx) => {
    if (formDisabled) return;
    setMarkers((prev) => prev.filter((_, i) => i !== idx));
  };

  const seekMarker = (tSec) => {
    videoRef?.current?.openPlayer?.();
    videoRef?.current?.seek?.(tSec);
  };

  const beginEdit = () => {
    if (readOnly) return;
    hydrateFromReview(integrityReview, { keepEditing: true });
    setEditing(true);
  };

  const cancelEdit = () => {
    hydrateFromReview(integrityReview);
    setEditing(false);
  };

  const save = async () => {
    if (!disposition) {
      toast.error('Choose an overall disposition before saving');
      return;
    }
    const depth = recordingAvailable ? reviewDepth : 'recording_unavailable';
    const codes = recordingAvailable
      ? tagCodes
      : tagCodes.filter((c) => NO_RECORDING_TAG_CODES.has(c));
    const markerPayload = recordingAvailable
      ? markers.map((m) => ({
        t_sec: m.t_sec,
        note: m.note || '',
        tag_code: m.tag_code || null,
      }))
      : [];
    setSaving(true);
    try {
      await v1Patch(patchPath, {
        catalog_version: INTEGRITY_CATALOG_VERSION,
        disposition,
        review_depth: depth,
        tag_codes: codes,
        notes,
        markers: markerPayload,
      });
      toast.success(submitted ? 'Integrity review updated' : 'Integrity review saved');
      setEditing(false);
      onChanged?.();
    } catch (e) {
      toastMutationError(e, 'Failed to save integrity review');
    } finally {
      setSaving(false);
    }
  };

  const saveDisabled = saving || !assessmentId || !disposition || formDisabled;

  const savedTags = Array.isArray(integrityReview?.tag_codes) ? integrityReview.tag_codes : [];
  const savedMarkers = Array.isArray(integrityReview?.markers) ? integrityReview.markers : [];

  const renderMarkersBlock = ({ interactive }) => {
    if (!recordingAvailable) return null;
    const list = interactive ? markers : savedMarkers;
    return (
      <div className="space-y-2 rounded-lg border bg-secondary/20 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-semibold">Time markers</p>
          {interactive && (
            <span className="text-[11px] tabular-nums text-muted-foreground">
              Now {formatIntegrityTime(currentTimeSec)}
            </span>
          )}
        </div>
        {interactive && !formDisabled && (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <div className="min-w-0 flex-1 space-y-1">
              <Label className="text-[11px]">Marker note</Label>
              <input
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={markerNote}
                onChange={(e) => setMarkerNote(e.target.value)}
                placeholder="What happened at this moment?"
                maxLength={500}
                data-testid={`${testIdPrefix}-integrity-marker-note`}
              />
            </div>
            <div className="w-full space-y-1 sm:w-48">
              <Label className="text-[11px]">Optional tag</Label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                value={markerTag}
                onChange={(e) => setMarkerTag(e.target.value)}
                data-testid={`${testIdPrefix}-integrity-marker-tag`}
              >
                <option value="">None</option>
                {INTEGRITY_TAGS.map((t) => (
                  <option key={t.code} value={t.code}>{t.label}</option>
                ))}
              </select>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={addMarker}
              disabled={!recordingAvailable}
              data-testid={`${testIdPrefix}-integrity-add-marker`}
            >
              <BookmarkPlus className="mr-1 h-3.5 w-3.5" />
              Mark now
            </Button>
          </div>
        )}
        {list.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            {interactive ? 'No markers yet.' : 'No markers recorded.'}
          </p>
        ) : (
          <ul className="space-y-1.5" data-testid={`${testIdPrefix}-integrity-markers`}>
            {list.map((m, idx) => {
              const tag = m.tag_code ? INTEGRITY_TAG_BY_CODE[m.tag_code] : null;
              return (
                <li
                  key={`${m.t_sec}-${idx}`}
                  className="flex flex-wrap items-center gap-2 rounded-md border bg-background px-2.5 py-1.5 text-xs"
                >
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 font-semibold tabular-nums text-foreground hover:underline"
                    onClick={() => seekMarker(m.t_sec)}
                    data-testid={`${testIdPrefix}-integrity-marker-${idx}`}
                  >
                    <MapPin className="h-3 w-3" />
                    {formatIntegrityTime(m.t_sec)}
                  </button>
                  {tag && (
                    <span className={classNames('rounded border px-1.5 py-0.5', severityChipClass(tag.severity))}>
                      {tag.label}
                    </span>
                  )}
                  {m.note && <span className="text-muted-foreground">{m.note}</span>}
                  {interactive && !formDisabled && (
                    <button
                      type="button"
                      className="ml-auto text-muted-foreground hover:text-foreground"
                      onClick={() => removeMarker(idx)}
                      aria-label="Remove marker"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  // Locked summary after a successful submit (until Edit review).
  if (submitted && !editing) {
    return (
      <Card className="card-elevated" data-testid={`${testIdPrefix}-integrity-review`}>
        <CardContent className="space-y-4 p-5">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <p className="text-sm font-semibold">Session integrity review</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Review is on record. Use Edit review to make changes.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <DispositionBadge disposition={integrityReview?.disposition || null} />
              {integrityReview?.reviewed_at && (
                <span className="text-[11px] text-muted-foreground" data-testid={`${testIdPrefix}-integrity-reviewed-at`}>
                  Reviewed {fmtDateTimeIST(integrityReview.reviewed_at)}
                </span>
              )}
              {!readOnly && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={beginEdit}
                  data-testid={`${testIdPrefix}-integrity-edit`}
                >
                  <Pencil className="mr-1 h-3.5 w-3.5" />
                  Edit review
                </Button>
              )}
            </div>
          </div>

          {renderMarkersBlock({ interactive: false })}

          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Tags by category</p>
            {savedTags.length === 0 ? (
              <p className="text-xs text-muted-foreground">No tags selected.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {savedTags.map((code) => {
                  const tag = INTEGRITY_TAG_BY_CODE[code];
                  if (!tag) return null;
                  return (
                    <span
                      key={code}
                      className={classNames('rounded-md border px-2 py-1 text-xs font-medium', severityChipClass(tag.severity))}
                      data-testid={`${testIdPrefix}-integrity-tag-${code}`}
                    >
                      {tag.label}
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <p className="text-xs font-semibold">Overall disposition</p>
              <DispositionBadge disposition={integrityReview?.disposition || null} />
            </div>
            <div className="space-y-1">
              <p className="text-xs font-semibold">Review depth</p>
              <p className="text-xs text-muted-foreground" data-testid={`${testIdPrefix}-integrity-depth-locked`}>
                {depthLabel(integrityReview?.review_depth)}
              </p>
            </div>
          </div>

          <div className="space-y-1">
            <p className="text-xs font-semibold">Notes</p>
            <p className="whitespace-pre-wrap text-xs text-muted-foreground" data-testid={`${testIdPrefix}-integrity-notes-locked`}>
              {integrityReview?.notes?.trim() ? integrityReview.notes : '—'}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-elevated" data-testid={`${testIdPrefix}-integrity-review`}>
      <CardContent className="space-y-4 p-5">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <p className="text-sm font-semibold">Session integrity review</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {editing
                ? 'Editing the saved review. Cancel to discard, or update to save changes.'
                : recordingAvailable
                  ? 'Manual safety tags for the whole recording, plus optional time markers for specific moments.'
                  : 'No recording was captured for this attempt — there is no footage to review. You can still note process issues and an overall disposition.'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <DispositionBadge disposition={integrityReview?.disposition || null} />
            {integrityReview?.reviewed_at && (
              <span className="text-[11px] text-muted-foreground" data-testid={`${testIdPrefix}-integrity-reviewed-at`}>
                Reviewed {fmtDateTimeIST(integrityReview.reviewed_at)}
              </span>
            )}
          </div>
        </div>

        {renderMarkersBlock({ interactive: true })}

        <div className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Tags by category</p>
          {visibleCategories.map((cat) => (
            <div key={cat} className="space-y-1.5">
              <p className="text-xs font-medium text-foreground">{cat}</p>
              <div className="flex flex-wrap gap-1.5">
                {(tagsByCategory[cat] || []).map((tag) => {
                  const selected = tagCodes.includes(tag.code);
                  return (
                    <button
                      key={tag.code}
                      type="button"
                      disabled={formDisabled}
                      onClick={() => toggleTag(tag.code)}
                      data-testid={`${testIdPrefix}-integrity-tag-${tag.code}`}
                      className={classNames(
                        'rounded-md border px-2 py-1 text-xs font-medium transition-colors',
                        selected ? severityChipClass(tag.severity) : 'bg-background text-muted-foreground hover:bg-secondary',
                        formDisabled && 'opacity-70 cursor-not-allowed',
                      )}
                    >
                      {tag.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-xs font-semibold">Overall disposition</Label>
            <div className="flex flex-wrap gap-1.5">
              {INTEGRITY_DISPOSITIONS.map((d) => (
                <button
                  key={d.value}
                  type="button"
                  disabled={formDisabled}
                  onClick={() => {
                    setDispTouched(true);
                    setDisposition(d.value);
                  }}
                  data-testid={`${testIdPrefix}-integrity-disposition-${d.value}`}
                  className={classNames(
                    'rounded-md border px-2.5 py-1 text-xs font-semibold',
                    disposition === d.value ? dispositionToneClass(d.value) : 'bg-background text-muted-foreground',
                    formDisabled && 'opacity-70 cursor-not-allowed',
                  )}
                >
                  {d.label}
                </button>
              ))}
            </div>
            {!dispTouched && recordingAvailable && tagCodes.length > 0 && !formDisabled && (
              <p className="text-[11px] text-muted-foreground">
                Suggested from tags: {suggested}
              </p>
            )}
            {!recordingAvailable && !disposition && !formDisabled && (
              <p className="text-[11px] text-muted-foreground">
                Choose a disposition to save this review.
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold">Review depth</Label>
            {recordingAvailable ? (
              <div className="flex flex-wrap gap-1.5">
                {INTEGRITY_REVIEW_DEPTHS.map((d) => (
                  <button
                    key={d.value}
                    type="button"
                    disabled={formDisabled}
                    onClick={() => setReviewDepth(d.value)}
                    data-testid={`${testIdPrefix}-integrity-depth-${d.value}`}
                    className={classNames(
                      'rounded-md border px-2.5 py-1 text-xs font-medium',
                      reviewDepth === d.value ? 'border-foreground/30 bg-secondary font-semibold' : 'bg-background text-muted-foreground',
                      formDisabled && 'opacity-70 cursor-not-allowed',
                    )}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            ) : (
              <p
                className="inline-flex rounded-md border border-foreground/30 bg-secondary px-2.5 py-1 text-xs font-semibold"
                data-testid={`${testIdPrefix}-integrity-depth-recording_unavailable`}
              >
                Recording unavailable
              </p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-xs font-semibold" htmlFor={`${testIdPrefix}-integrity-notes`}>Notes</Label>
          <Textarea
            id={`${testIdPrefix}-integrity-notes`}
            data-testid={`${testIdPrefix}-integrity-notes`}
            value={notes}
            disabled={formDisabled}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={
              recordingAvailable
                ? 'Summarize integrity observations for this session…'
                : 'Note why recording is missing or any process concerns…'
            }
            rows={3}
            maxLength={4000}
          />
        </div>

        {!formDisabled && (
          <div className="flex flex-wrap justify-end gap-2">
            {editing && (
              <Button
                type="button"
                variant="outline"
                onClick={cancelEdit}
                disabled={saving}
                data-testid={`${testIdPrefix}-integrity-cancel`}
              >
                Cancel
              </Button>
            )}
            <Button
              onClick={save}
              disabled={saveDisabled}
              data-testid={`${testIdPrefix}-integrity-save`}
            >
              <Save className="mr-1.5 h-3.5 w-3.5" />
              {saving
                ? (editing ? 'Updating…' : 'Saving…')
                : (editing ? 'Update integrity review' : 'Save integrity review')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
